/**
 * Feb 28, 2026 · Sales Order Dispatch History report.
 *
 * Pick an SO/ESO → per-line chronological shipment feed (PS · PL · DC · GST
 * Invoice · Qty) + per-line totals + Total Summary. PDF export handled by
 * the backend.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import { ArrowsClockwiseIcon, PrinterIcon, MagnifyingGlassIcon, PackageIcon } from "@phosphor-icons/react";
import { fmtDateIST } from "@/lib/datetime";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function DispatchHistory({ defaultSource = "SO", lockSource = false, title = "Sales Order Dispatch History", subtitle = "Chronological shipment feed per line item · with PDF export for the customer.", testIdPrefix = "dispatch-history" }) {
  const [source, setSource] = useState(defaultSource);
  const [q, setQ] = useState("");
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Mar 1, 2026 · Slice-the-grid filters (Customer · Status · Date Range)
  const [customerFilter, setCustomerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    // Preload SO + ESO rosters with rich metadata (date, customer name,
    // PO, delivery date, status) so the main grid can mirror the Sales
    // Orders master table + drill down on click.
    Promise.all([
      api.get("/sales-orders/"),
      api.get("/exports/orders/"),
      api.get("/customers/").catch(() => ({ data: [] })),
    ])
      .then(([s, e, c]) => {
        const cmap = {}; (c.data || []).forEach(x => { cmap[x.id] = x.name || ""; });
        setOrders([
          ...(s.data || []).map(o => ({
            source: "SO", id: o.id, no: o.order_no,
            customer_id: o.customer_id, customer_name: cmap[o.customer_id] || "—",
            order_date: o.order_date || "", delivery_date: o.delivery_date || "",
            po_number: o.po_number || "", status: o.status || "Open", items: o.items || [],
          })),
          ...(e.data || []).map(o => ({
            source: "ESO", id: o.id, no: o.doc_no || o.order_no,
            customer_id: o.buyer_customer_id || o.customer_id,
            customer_name: cmap[o.buyer_customer_id || o.customer_id] || o.buyer_name || "—",
            order_date: o.order_date || "", delivery_date: o.delivery_date || "",
            po_number: o.buyer_po_no || o.pi_no || "", status: o.status || "Open", items: o.items || [],
          })),
        ]);
      })
      .catch(() => setOrders([]));
  }, []);

  // Customer + status options are derived from the loaded roster so the
  // dropdowns are always in-sync with what's actually on the grid for
  // the active source (SO vs ESO).
  const { customerOptions, statusOptions } = useMemo(() => {
    const cs = new Set(), ss = new Set();
    orders.filter(o => o.source === source).forEach(o => {
      if (o.customer_name && o.customer_name !== "—") cs.add(o.customer_name);
      if (o.status) ss.add(o.status);
    });
    return {
      customerOptions: Array.from(cs).sort((a, b) => a.localeCompare(b)),
      statusOptions:   Array.from(ss).sort((a, b) => a.localeCompare(b)),
    };
  }, [orders, source]);

  const filtered = useMemo(() => {
    const lo = q.trim().toLowerCase();
    const from = dateFrom || "";
    const to   = dateTo || "";
    return orders.filter(o => {
      if (o.source !== source) return false;
      if (lo && !(o.no || "").toLowerCase().includes(lo)) return false;
      if (customerFilter && o.customer_name !== customerFilter) return false;
      if (statusFilter && (o.status || "") !== statusFilter) return false;
      // Order date range — inclusive on both sides. Dates on the roster
      // are ISO-8601 (YYYY-MM-DD) so lex compare = chrono compare.
      const od = (o.order_date || "").slice(0, 10);
      if (from && od && od < from) return false;
      if (to   && od && od > to)   return false;
      // If the row has no order_date but a range is set, drop it — ops
      // are asking for a dated window and undated rows are noise.
      if ((from || to) && !od) return false;
      return true;
    });
  }, [orders, source, q, customerFilter, statusFilter, dateFrom, dateTo]);

  const activeFilterCount =
    (customerFilter ? 1 : 0) + (statusFilter ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);
  const clearFilters = () => { setCustomerFilter(""); setStatusFilter(""); setDateFrom(""); setDateTo(""); };

  const load = async (o) => {
    setSelected(o); setLoading(true); setData(null);
    try {
      const { data } = await api.get("/dispatch-history", { params: { source: o.source, order_id: o.id } });
      setData(data);
    } finally { setLoading(false); }
  };

  const printPdf = async () => {
    if (!selected) return;
    const res = await api.get("/dispatch-history/pdf", { params: { source: selected.source, order_id: selected.id }, responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
  };

  return (
    <div className="p-6 space-y-5" data-testid={`${testIdPrefix}-page`}>
      <div>
        <div className="overline text-zinc-500 mb-1">Reports</div>
        <h1 className="font-display text-2xl">{title}</h1>
        <p className="text-xs text-zinc-400 mt-1">{subtitle}</p>
      </div>

      {/* Search */}
      <div className="tactical-card p-3 flex items-end gap-3 flex-wrap" data-testid="dh-search">
        <div>
          <div className="overline text-[10px] mb-1">Source</div>
          <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden">
            {["SO", "ESO"].map(v => (
              <button key={v} data-testid={`dh-src-${v}`} onClick={() => !lockSource && setSource(v)}
                disabled={lockSource && v !== defaultSource}
                className={`px-3 py-2 text-xs font-semibold tracking-wide ${source === v ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"} ${lockSource && v !== defaultSource ? "opacity-30 cursor-not-allowed" : ""}`}>{v}</button>
            ))}
          </div>
        </div>
        <div className="min-w-[320px] flex-1">
          <div className="overline text-[10px] mb-1">{source} No</div>
          <div className="relative">
            <MagnifyingGlassIcon size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input data-testid="dh-order-search" value={q} onChange={e => setQ(e.target.value)}
              placeholder={`Search ${source} number…`}
              className="w-full h-9 pl-7 pr-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)] font-mono" />
          </div>
        </div>
        <div className="min-w-[180px]">
          <div className="overline text-[10px] mb-1">Customer</div>
          <select data-testid="dh-customer-filter" value={customerFilter}
            onChange={e => setCustomerFilter(e.target.value)}
            className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm">
            <option value="">All customers</option>
            {customerOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="min-w-[140px]">
          <div className="overline text-[10px] mb-1">Status</div>
          <select data-testid="dh-status-filter" value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm">
            <option value="">All statuses</option>
            {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <div className="overline text-[10px] mb-1">Order Date · From</div>
          <input type="date" data-testid="dh-date-from" value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" />
        </div>
        <div>
          <div className="overline text-[10px] mb-1">To</div>
          <input type="date" data-testid="dh-date-to" value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" />
        </div>
        {activeFilterCount > 0 && (
          <button data-testid="dh-clear-filters" onClick={clearFilters}
            className="h-9 px-3 border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 text-xs font-mono uppercase tracking-widest rounded-sm">
            Clear · {activeFilterCount}
          </button>
        )}
        {selected && (
          <button data-testid="dh-refresh" onClick={() => load(selected)} disabled={loading}
            className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-xs rounded-sm flex items-center gap-1.5">
            <ArrowsClockwiseIcon size={12} weight="bold" />Refresh
          </button>
        )}
        {data && (
          <button data-testid="dh-print" onClick={printPdf}
            className="h-9 px-3 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-sm flex items-center gap-1.5">
            <PrinterIcon size={12} weight="bold" />Print PDF
          </button>
        )}
      </div>

      {/* Order list — shown when nothing selected OR user is searching / filtering */}
      {(!selected || q || activeFilterCount > 0) && (
        <div className="tactical-card overflow-x-auto max-h-[65vh]" data-testid="dh-order-list">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)] sticky top-0 z-10 bg-[var(--bg-1)]">
              <tr>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Order No</th>
                <th className="text-left px-3 py-2">Order Date</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Customer PO</th>
                <th className="text-left px-3 py-2">Style</th>
                <th className="text-right px-3 py-2">Items</th>
                <th className="text-right px-3 py-2" data-testid="dh-order-qty-header"
                  title={(source || "").toUpperCase() === "ESO" ? "Customer Qty − Cancelled (buffer excluded)" : "Order Qty + Dispatch Buffer − Cancelled · sealed historical Dispatch History treatment"}>
                  {(source || "").toUpperCase() === "ESO" ? "Order Qty" : "Order Qty · incl. buffer"}</th>
                <th className="text-left px-3 py-2">Delivery Date</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-zinc-500">No {source} orders match your search or filters.</td></tr>}
              {filtered.map(o => {
                const items = o.items || [];
                const firstItem = items[0] || {};
                // DP-ESO-REPORT-QTY-01 · Mar 2026 · Owner Rule — Buffer /
                // Additional Qty (`extra_qty`) is a production allowance
                // and MUST NOT be surfaced as Order Qty on ESO reports.
                // Domestic SO retains its sealed `order_qty + extra_qty`
                // semantic; ESO uses customer `qty` only.
                const totalQty = items.reduce((s, li) => {
                  const baseQty = Number(li.order_qty ?? li.qty ?? li.quantity) || 0;
                  const bufferQty = Number(li.extra_qty) || 0;
                  const cancelledQty = Number(li.cancelled_qty) || 0;
                  const isEso = (source || "").toUpperCase() === "ESO";
                  return s + baseQty + (isEso ? 0 : bufferQty) - cancelledQty;
                }, 0);
                const styles = Array.from(new Set(items.map(li => li.style_no).filter(Boolean))).slice(0, 3).join(", ");
                return (
                  <tr key={o.id} onClick={() => { setQ(""); load(o); }}
                    className={`border-b border-[var(--border)]/40 cursor-pointer hover:bg-[var(--accent)]/5 ${selected?.id === o.id ? "bg-[var(--accent)]/10" : ""}`}
                    data-testid={`dh-order-${o.no}`}>
                    <td className="px-3 py-2"><span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-mono font-semibold ${o.source === "SO" ? "bg-blue-500/15 text-blue-300" : "bg-sky-500/15 text-sky-300"}`}>{o.source}</span></td>
                    <td className="px-3 py-2 font-mono text-white">{o.no}</td>
                    <td className="px-3 py-2 font-mono text-zinc-400 text-xs">{o.order_date || "—"}</td>
                    <td className="px-3 py-2 text-zinc-300 text-xs truncate max-w-[220px]" title={o.customer_name}>{o.customer_name || "—"}</td>
                    <td className="px-3 py-2 font-mono text-zinc-400 text-xs">{o.po_number || "—"}</td>
                    <td className="px-3 py-2 font-mono text-zinc-300 text-xs truncate max-w-[140px]" title={styles}>{styles || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-300 text-xs">{items.length}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-white">{fmt(totalQty)}</td>
                    <td className="px-3 py-2 font-mono text-zinc-400 text-xs">{firstItem.delivery_date || o.delivery_date || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-widest ${(o.status || "").toLowerCase() === "closed" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" : "bg-amber-500/15 text-amber-300 border border-amber-500/40"}`}>{o.status || "Open"}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Back button when in drill-down mode */}
      {selected && !q && activeFilterCount === 0 && (
        <button data-testid="dh-back" onClick={() => { setSelected(null); setData(null); }}
          className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-xs rounded-sm inline-flex items-center gap-1.5">
          ← Back to Order List
        </button>
      )}

      {/* Detail */}
      {selected && data && (
        <div className="space-y-4" data-testid="dh-detail">
          <div className="tactical-card p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Order</div>
                <div className="font-display text-xl">{data.order.order_no}</div>
                <div className="text-xs text-zinc-400 mt-1">Customer: <span className="text-white">{data.order.customer}</span> · Status: <span className="text-white">{data.order.status || "—"}</span> · Order Date: <span className="text-white">{data.order.order_date || "—"}</span></div>
              </div>
              <div className="text-right font-mono text-xs" data-testid="dh-totals">
                <div className="text-zinc-500">Ordered <span className="text-white text-lg">{fmt(data.totals.order_qty)}</span> m</div>
                <div className="text-zinc-500">Shipped <span className="text-emerald-300 text-lg">{fmt(data.totals.shipped)}</span> m</div>
                <div className="text-zinc-500">Remaining <span className={`text-lg ${data.totals.remaining > 0 ? "text-amber-300" : "text-emerald-400"}`}>{fmt(data.totals.remaining)}</span> m</div>
                {/* DP-ESO-REPORT-QTY-01 · Owner Rule #4-#5 — Excess
                    Supplied is a customer-facing metric surfaced even
                    when the over-dispatch is within Buffer allowance.
                    Line-level first, then aggregated (not netted). */}
                {(Number(data.totals.excess_supplied) || 0) > 0 && (
                  <div className="text-zinc-500" data-testid="dh-total-excess-supplied">
                    Excess Supplied <span className="text-orange-300 text-lg">{fmt(data.totals.excess_supplied)}</span> m
                  </div>
                )}
                <div className="text-[10px] text-zinc-500 mt-1">{data.totals.shipments} shipment(s)</div>
              </div>
            </div>
          </div>

          {loading && <div className="text-center py-6 text-zinc-500">Loading…</div>}

          {data.lines.length === 0 && !loading && <div className="tactical-card text-center py-12 text-zinc-500">No line items with shipments yet.</div>}

          {data.lines.map((ln, i) => (
            <div key={i} className="tactical-card overflow-x-auto" data-testid={`dh-line-${ln.product_code}`}>
              {/* Feb 28, 2026 · Per-line header row · SO No · SO Date · Customer · Item · Order Qty */}
              <div className="p-3 bg-[var(--bg-2)]/50 border-b border-[var(--border)]" data-testid={`dh-line-header-${ln.product_code}`}>
                <div className="grid grid-cols-6 gap-3 items-baseline">
                  <div>
                    <div className="overline text-[9px] mb-0.5">{data.order.source} No</div>
                    <div className="font-mono font-semibold text-amber-300 text-sm">{data.order.order_no}</div>
                  </div>
                  <div>
                    <div className="overline text-[9px] mb-0.5">{data.order.source} Date</div>
                    <div className="font-mono text-white text-sm">{data.order.order_date || "—"}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="overline text-[9px] mb-0.5">Customer</div>
                    <div className="text-white text-sm truncate" title={data.order.customer}>{data.order.customer}</div>
                  </div>
                  <div>
                    <div className="overline text-[9px] mb-0.5">Item</div>
                    <div className="font-mono text-[var(--accent)] text-sm truncate" title={ln.product_code}>{ln.product_code || "—"}</div>
                    {ln.style_no && <div className="text-[10px] text-zinc-500 font-mono">{ln.style_no}</div>}
                  </div>
                  <div className="text-right">
                    <div className="overline text-[9px] mb-0.5">Order Qty</div>
                    <div className="font-mono font-semibold text-white text-sm">{fmt(ln.order_qty)} <span className="text-[10px] text-zinc-500">{ln.unit}</span></div>
                    <div className="text-[10px] font-mono mt-0.5">
                      <span className="text-emerald-300">↑ {fmt(ln.total_shipped)}</span>
                      <span className="text-zinc-600 mx-1">·</span>
                      <span className={ln.remaining > 0 ? "text-amber-300" : "text-emerald-400"}>◇ {fmt(ln.remaining)}</span>
                      {(Number(ln.excess_supplied) || 0) > 0 && (
                        <>
                          <span className="text-zinc-600 mx-1">·</span>
                          <span className="text-orange-300" data-testid={`dh-line-excess-supplied-${ln.product_code}`} title="Excess Supplied = Actual Dispatched − Order Qty">⇑ {fmt(ln.excess_supplied)}</span>
                        </>
                      )}
                    </div>
                    {ln.delivery_date && (
                      <div
                        data-testid={`dh-line-req-delivery-${ln.product_code}`}
                        className={`text-[10px] font-mono mt-0.5 ${ln.delivery_date < new Date().toISOString().slice(0,10) && ln.remaining > 0 ? "text-red-400 font-semibold" : "text-amber-300"}`}
                        title="Item-wise required delivery date · line-level benchmark"
                      >
                        Req Delivery: {ln.delivery_date}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500 bg-[var(--bg-2)]/20">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    {data.order.source === "ESO" && <th className="text-left px-3 py-2">Product Code</th>}
                    <th className="text-left px-3 py-2">PL No</th>
                    <th className="text-left px-3 py-2">DC No</th>
                    <th className="text-left px-3 py-2">Invoice No</th>
                    <th className="text-left px-3 py-2">Transport</th>
                    <th className="text-left px-3 py-2">LR No</th>
                    <th className="text-right px-3 py-2">Shipped Qty (m)</th>
                  </tr>
                </thead>
                <tbody>
                  {ln.shipments.length === 0 && <tr><td colSpan={data.order.source === "ESO" ? 8 : 7} className="text-center py-4 text-zinc-500 italic">No shipments yet</td></tr>}
                  {ln.shipments.map((s, j) => (
                    <tr key={j} className="border-b border-[var(--border)]/30 hover:bg-[var(--bg-2)]/40" data-testid={`dh-ship-${ln.product_code}-${j}`}>
                      <td className="px-3 py-2 font-mono text-zinc-300">{fmtDateIST(s.date)}</td>
                      {data.order.source === "ESO" && (
                        <td className="px-3 py-2 font-mono text-[var(--accent)] truncate max-w-[220px]" title={s.product_code || ln.product_code} data-testid={`dh-ship-item-${ln.product_code}-${j}`}>{s.product_code || ln.product_code || "—"}</td>
                      )}
                      <td className="px-3 py-2 font-mono text-zinc-400 whitespace-nowrap">{s.pl_no || "—"}</td>
                      <td className="px-3 py-2 font-mono text-sky-300 whitespace-nowrap">{s.dc_no || "—"}</td>
                      <td className="px-3 py-2 font-mono text-emerald-300 whitespace-nowrap">{s.invoice_no || "—"}</td>
                      <td className="px-3 py-2 text-zinc-300 truncate max-w-[180px]" title={[s.transporter, s.vehicle_no].filter(Boolean).join(" · ")}>
                        <div className="font-mono truncate">{s.transporter || "—"}</div>
                        {s.vehicle_no && <div className="text-[9px] font-mono text-zinc-500 truncate">{s.vehicle_no}</div>}
                      </td>
                      <td className="px-3 py-2 font-mono text-zinc-400">{s.lr_no || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {fmt(s.shipped_qty)}
                        {s.is_excess && <span className="ml-1 px-1 py-0.5 rounded-sm text-[9px] font-mono bg-amber-500/15 text-amber-300" title="Authorized Excess">AUTH. EXCESS</span>}
                        {s.slip_count > 1 && <span className="ml-1 px-1 py-0.5 rounded-sm text-[9px] font-mono bg-zinc-700/40 text-zinc-300" title={`Aggregated across ${s.slip_count} Packing Slips`}>×{s.slip_count} PS</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[var(--border)] bg-[var(--bg-2)]/40">
                    <td colSpan={data.order.source === "ESO" ? 7 : 6} className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Line Sub-Total · Shipped / Remaining</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">
                      <span className="text-emerald-300">{fmt(ln.total_shipped)}</span> / <span className={ln.remaining > 0 ? "text-amber-300" : "text-emerald-400"}>{fmt(ln.remaining)}</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
